import { memo } from 'react';
import { VoteIndicator as VoteIndicatorIcon } from '@mentimeter/ragnar-visuals';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Tooltip } from '@mentimeter/ragnar-ui/tooltip';
import { useTranslations } from 'next-intl';
import { IndicationNumber } from '../IndicationNumbers';
import { Indication } from '../Indication';
import { getVisibleAudienceMemberCount } from '../../../utils/getVisibleAudienceMemberCount';
import { getCompactNumberFormat } from './getCompactNumberFormat';

interface RespondentsIndicatorProps {
  respondentCount: number | undefined;
  audienceMemberCount: number;
}

function getIndicationNumberValue(
  respondentCount: number,
  audienceMemberCount: number,
  visibleAudienceMemberCount: number,
) {
  if (audienceMemberCount === 0) {
    return getCompactNumberFormat(respondentCount);
  }

  return (
    <>
      <Text color="inherit" lineHeight="inherit" fontWeight="semiBold">
        {getCompactNumberFormat(respondentCount)}
      </Text>
      <Text
        color="inherit"
        lineHeight="inherit"
        fontWeight="regular"
        extend={() => ({
          opacity: 0.55,
        })}
      >
        {' '}
        /{' '}
      </Text>
      <Text color="inherit" lineHeight="inherit" fontWeight="semiBold">
        {getCompactNumberFormat(visibleAudienceMemberCount)}
      </Text>
    </>
  );
}

export const RespondentsIndicator = memo<RespondentsIndicatorProps>(
  ({ respondentCount, audienceMemberCount }: RespondentsIndicatorProps) => {
    const translate = useTranslations('presenting');

    if (respondentCount === undefined) {
      return (
        <Tooltip
          sideOffset={20}
          withPortal={false}
          align="end"
          trigger={
            <Box data-testid="vote-indicator">
              <Indication>
                <IndicationNumber value={audienceMemberCount} />
                <VoteIndicatorIcon size={3} color="text" />
              </Indication>
            </Box>
          }
        >
          {translate('indications.participants_active_message')}
        </Tooltip>
      );
    }

    const visibleAudienceMemberCount = getVisibleAudienceMemberCount({
      respondentCount,
      audienceMemberCount,
    });

    const indicationNumberValue = getIndicationNumberValue(
      respondentCount,
      audienceMemberCount,
      visibleAudienceMemberCount,
    );
    const shouldShowCounts = audienceMemberCount > 0 || respondentCount > 0;

    const tooltipText =
      audienceMemberCount > 0
        ? translate('indications.participants_responded_of_active_message')
        : translate('indications.participants_responded_message');

    return (
      <Tooltip
        sideOffset={20}
        withPortal={false}
        align="end"
        trigger={
          <Box data-testid="vote-indicator">
            <Indication>
              <IndicationNumber
                value={{
                  customComponent: indicationNumberValue,
                  shouldShow: shouldShowCounts,
                }}
              />
              <VoteIndicatorIcon size={3} color="text" />
            </Indication>
          </Box>
        }
      >
        {shouldShowCounts && tooltipText}
      </Tooltip>
    );
  },
);
